import { useEffect } from "react";
import { Button, Division, DropZone, Flex, Form, Icon, Icons, Paragraph, Select, Spin, useToast } from "@ir/ui";
import classNames from "classnames";
import { DocumentMimeType } from "@/core/enums/DocumentMimeType";
import { useCreditDocumentUpload } from "./hook";
import { Credit } from "@/core/entities/credit";
import { ICreditAttachDocumentsParams } from "@/core/repositories/credit";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { CreditRepository } from "@/infrastructure/repositories/credit";
import { useDeductionDrawer } from "@/presentation/deduction/hooks/drawer/useDeductionDrawer";
import { useLookup } from "@/hooks";
import { AttachCreditDocumentsUseCase } from "@/core/usecases/credit";
import { CreditUploadDocument } from "./atom/documentUpload";
import { useAtomValue } from "jotai";
import { groupByCheckAtom } from "@/store/deduction/main";
import { currentPageAtom as currentRemittancePageAtom } from "@/store/deduction/remittance";
import { currentPageAtom as currentRemittanceItemPageAtom } from "@/store/deduction/remittanceItem";
import { mapCreditDocumentsToViewerDocuments } from "../utils/mapCreditDocumentsToViewerDocuments";
import {
  buildCreditAttachFiles,
  getAttachableCreditDocuments,
  isAttachableCreditDocument,
} from "./creditDocumentUpload.utils";

const FILE_CONSTANTS = {
  MAX_LENGTH: 10,
  MAX_SIZE_MB: 2,
  MAX_FILE_NAME_LENGTH: 20,
  ACCEPTED_TYPES: [
    DocumentMimeType.PDF,
    DocumentMimeType.EXCEL,
    DocumentMimeType.EXCEL_LEGACY,
    DocumentMimeType.CSV,
    DocumentMimeType.PLAIN_TEXT,
    DocumentMimeType.WORD,
    DocumentMimeType.WORD_DOCX,
    DocumentMimeType.JPEG,
    DocumentMimeType.PNG,
    DocumentMimeType.GIF,
    DocumentMimeType.TIFF,
    DocumentMimeType.BMP,
    DocumentMimeType.EDI,
  ],
  ACCEPTED_EXTENSIONS: ["pdf", "xlsx", "xls", "csv", "txt", "doc", "docx", "jpg", "png", "gif", "tiff", "bmp", "edi"],
};

interface IDocumentUploadFormProps {
  attachDocumentTrigger: number | null;
  credit: Credit;
  onClose: () => void;
  isDocumentAttached: boolean;
  setIsDocumentAttached: (isDocumentAttached: boolean) => void;
}

interface IFormDocument {
  processId: number;
  documentType: { label: string; value: string } | null;
}

interface IFormData {
  documents: IFormDocument[];
}

const getFileType = (file: File): DocumentMimeType => {
  const isEdiFile = file.name?.toLowerCase().endsWith(".edi");
  return isEdiFile ? DocumentMimeType.EDI : (file.type.toLowerCase() as DocumentMimeType);
};

const sanitizeFileName = (filename: string): string => {
  const sanitized = filename
    .replaceAll(/[<>:"/\\|?*]/g, "")
    .replaceAll(/\s+/g, "_")
    .replaceAll(/[^a-zA-Z0-9._-]/g, "")
    .replaceAll(/^\.+/g, "")
    .replaceAll(/\.{2,}/g, ".")
    .trim();

  return sanitized || "untitled";
};

const getFileSize = (file: File): number => file.size / 1024 / 1024;

const getFileSizeInString = (file: File): string => {
  const sizeInMB = getFileSize(file);
  return sizeInMB < 1 ? `${(file.size / 1024).toFixed(2)} KB` : `${sizeInMB.toFixed(2)} MB`;
};

const validateFile = (file: File): { error: string | null; isValid: boolean } => {
  const fileSize = getFileSize(file);

  if (fileSize > FILE_CONSTANTS.MAX_SIZE_MB) {
    return { error: `File size must be less than ${FILE_CONSTANTS.MAX_SIZE_MB}MB`, isValid: false };
  }

  if (!FILE_CONSTANTS.ACCEPTED_TYPES.includes(getFileType(file))) {
    return { error: "File type is not supported", isValid: false };
  }

  return { error: null, isValid: true };
};

const DocumentUploadForm = ({ attachDocumentTrigger, credit, onClose, isDocumentAttached, setIsDocumentAttached }: IDocumentUploadFormProps) => {
  const [creditUploadForm] = Form.useForm<IFormData>();
  const { error: showErrorToast } = useToast();
  const { documentTypesLookup, getDocumentTypesLookup } = useLookup();
  const { documents, addDocumentUpload, uploadDocument, removeDocument, setIsAttaching, clearDocumentUpload, updateCredit } = useCreditDocumentUpload();
  const { setViewerDocuments } = useDeductionDrawer();

  const groupByCheck = useAtomValue(groupByCheckAtom);
  const currentRemittancePage = useAtomValue(currentRemittancePageAtom);
  const currentRemittanceItemPage = useAtomValue(currentRemittanceItemPageAtom);
  const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;
  const activeDocuments = documents.filter(document => !document.error);

  useEffect(() => {
    getDocumentTypesLookup();
  }, [getDocumentTypesLookup]);

  const handleOnDrop = async (files: File[]) => {
    const currentFileLength = activeDocuments.length;
    const filesToUpload = files.slice(0, FILE_CONSTANTS.MAX_LENGTH - currentFileLength);

    await addDocumentUpload(
      filesToUpload.map((file: File, index: number) => {
        const newFile = new File([file], sanitizeFileName(file.name), { type: getFileType(file) });
        const { error, isValid } = validateFile(newFile);

        return {
          processId: Date.now() + index,
          file: newFile,
          isPending: isValid,
          isUploading: false,
          isUploaded: false,
          error,
          response: null,
          type: null,
        };
      })
    );

    await uploadDocument();
  };

  useEffect(() => {
    if (attachDocumentTrigger) {
      creditUploadForm.submit();
    }
  }, [attachDocumentTrigger, creditUploadForm]);

  useEffect(() => {
    return () => {
      creditUploadForm.resetFields();
      clearDocumentUpload();
      setIsDocumentAttached(false);
    };
  }, [clearDocumentUpload, setIsDocumentAttached, creditUploadForm]);

  const doAttachDocuments = async () => {
    setIsAttaching(true);
    const currentFormDocuments: IFormDocument[] = creditUploadForm.getFieldValue(["documents"]) ?? [];
    const files = buildCreditAttachFiles(documents, currentFormDocuments);

    if (files.length === 0) {
      showErrorToast({
        content: "No valid documents to attach. Remove invalid files or select a document type.",
      });
      setIsAttaching(false);
      return;
    }

    const attachDocumentsRequest: ICreditAttachDocumentsParams = {
      remittanceItemId: credit.remittanceItemId,
      files,
    };

    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const attachCreditDocumentsUseCase = new AttachCreditDocumentsUseCase(new CreditRepository(client));
      const response = await attachCreditDocumentsUseCase.execute(attachDocumentsRequest, currentPage);

      updateCredit(response.attachCreditDocument);
      setIsDocumentAttached(true);
      setViewerDocuments(mapCreditDocumentsToViewerDocuments(response.attachCreditDocument.documents));
      setTimeout(onClose, 1000);
    } catch {
      showErrorToast({
        content: "Failed to attach documents",
      });
    } finally {
      setIsAttaching(false);
    }
  };

  const removeDocumentFromForm = (processId: number) => {
    const currentFormDocuments = creditUploadForm.getFieldValue(["documents"]);
    creditUploadForm.setFieldsValue({
      documents: currentFormDocuments.filter((d: IFormDocument) => d.processId !== processId),
    });
    removeDocument(processId);
  };

  const getSortedDocuments = (uploadDocuments: CreditUploadDocument[]) => {
    return uploadDocuments.reverse();
  };

  useEffect(() => {
    const oldFormDocuments = creditUploadForm.getFieldValue(["documents"]) ?? [];
    const newFormDocuments = [...oldFormDocuments];
    const sortedDocuments = getSortedDocuments(documents);
    for (const d of sortedDocuments) {
      if (!newFormDocuments.some(f => f.processId === d.processId)) {
        newFormDocuments.unshift({
          processId: d.processId,
          documentType: null,
        });
      }
    }

    creditUploadForm.setFieldsValue({
      documents: newFormDocuments,
    });
  }, [documents, creditUploadForm]);

  const renderDocumentItem = (eachField: { name: number }, currentDocument: CreditUploadDocument | undefined) => {
    if (!currentDocument) return null;

    const fileName = currentDocument?.file.name;
    const extension = fileName?.split(".").pop();
    const nameWithoutExtension = fileName?.substring(0, fileName?.lastIndexOf("."));
    const displayName =
      nameWithoutExtension && nameWithoutExtension.length > FILE_CONSTANTS.MAX_FILE_NAME_LENGTH
        ? `${nameWithoutExtension.substring(0, FILE_CONSTANTS.MAX_FILE_NAME_LENGTH)}...${extension}`
        : fileName;

    return (
      <Flex
        key={eachField.name}
        direction="column"
        align="stretch"
        className={classNames("p-0 border-1 border-color-grey-50 mt-2", {
          "border-color-error-500": currentDocument.error,
          "border-color-success-500": currentDocument.isUploaded,
          "border-color-warning-500": currentDocument.isPending && !currentDocument.error && !currentDocument.isUploading,
        })}
        style={{ borderRadius: 8 }}
      >
        <Flex direction="row" gap={1} align="center" className="p-2 border-bottom-1 border-color-grey-50">
          <Flex align="center" justifyContent="center">
            <Icon name={Icons.File} color={currentDocument.error ? "error" : "grey"} />
          </Flex>
          <Flex className="flex-1" direction="column">
            <Paragraph className="m-0 paragraph text-grey-700 text-md" style={{ whiteSpace: "nowrap" }} title={fileName}>
              {displayName}
            </Paragraph>
            {currentDocument?.error && <Paragraph className="text-xs text-error-500 m-0">{currentDocument.error}</Paragraph>}
            <Paragraph className="text-xs text-grey-400 m-0">{getFileSizeInString(currentDocument?.file)}</Paragraph>
          </Flex>
          <Flex align="center" justifyContent="center">
            <Spin size="medium" spinning={currentDocument.isUploading} />
          </Flex>
          <Flex align="center" justifyContent="center">
            <Button
              isDisabled={currentDocument.isUploading ?? isDocumentAttached}
              variant="clear"
              color="grey"
              onPress={() => removeDocumentFromForm(currentDocument.processId)}
            >
              <Icon name={Icons.Close} color="grey" />
            </Button>
          </Flex>
        </Flex>
        {!currentDocument?.error && (
          <Flex className="p-2">
            <Form.Item
              name={[eachField.name, "documentType"]}
              label="Document type"
              rules={[{ required: !currentDocument?.error, message: "Document type is required" }]}
              className="w-100 mb-0"
            >
              <Select
                size="medium"
                menuPlacement="top"
                isClearable={false}
                isSearchable={false}
                options={documentTypesLookup?.data?.map(documentType => ({
                  label: documentType.typeName,
                  value: documentType.id.toString(),
                }))}
                isLoading={documentTypesLookup.isLoading}
              />
            </Form.Item>
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <Form form={creditUploadForm} onFinish={doAttachDocuments} className="m-0">
      <DropZone
        onDrop={handleOnDrop}
        description={
          <Paragraph>
            Accepted file types: <b>{FILE_CONSTANTS.ACCEPTED_EXTENSIONS.join(", ")}</b> only. You can upload upto {FILE_CONSTANTS.MAX_LENGTH} files, with a
            maximum size of {FILE_CONSTANTS.MAX_SIZE_MB}MB per file
          </Paragraph>
        }
        allowedFileTypesOnSelect={FILE_CONSTANTS.ACCEPTED_TYPES}
        isDisabled={activeDocuments.length >= FILE_CONSTANTS.MAX_LENGTH || documents.some(d => d.isPending || d.isUploading)}
        overlay={isDocumentAttached && <Icon name={Icons.CheckCircle} color="grey" style={{ width: "45px", height: "45px" }} className="text-success-500" />}
        overlayClassName="bg-grey-75-transparent"
      />

      <Division>
        <Form.List
          name="documents"
          rules={[
            {
              validator: async (_, list) => {
                const hasAttachableDocument = getAttachableCreditDocuments(documents).length > 0;
                const missingDocumentType = documents.some(document => {
                  if (!isAttachableCreditDocument(document)) {
                    return false;
                  }

                  return !list?.find((formDocument: IFormDocument) => formDocument.processId === document.processId)?.documentType;
                });

                if (!hasAttachableDocument) {
                  throw new Error("At least 1 valid document is required");
                }

                if (missingDocumentType) {
                  throw new Error("Document type is required for all valid files");
                }
              },
            },
          ]}
        >
          {(fields, _, { errors }) => (
            <>
              {fields.map(eachField => {
                const currentFormDocument = creditUploadForm.getFieldValue(["documents", eachField.name]);
                const currentDocument = documents.find(d => d.processId === currentFormDocument.processId);
                return renderDocumentItem(eachField, currentDocument);
              })}
              <Form.ItemError>{errors}</Form.ItemError>
            </>
          )}
        </Form.List>
      </Division>
    </Form>
  );
};

export default DocumentUploadForm;
